// Shared by Cloud Functions and the frontend (imported there as @shared).
//
// Validates a league's team list (from a CSV upload, or generated) and
// turns it into conferences and teams. The browser runs this for a preview;
// the server runs it again before saving.

import {CONFERENCE_MAX, CONFERENCE_MIN} from "./leagueTypes.js";
import {cleanState} from "./places.js";
import {slugify} from "./slug.js";

// city and state are optional (older team lists don't have them).
export const TEAM_CSV_COLUMNS = [
  "conference", "name", "mascot", "abbreviation", "color1", "color2",
  "prestige", "city", "state",
];

export const TEAM_CSV_TEMPLATE = [
  TEAM_CSV_COLUMNS.join(","),
  "Lakeshore Conference,Glenbrook,Hawks,GLB,#1d4ed8,#facc15,4," +
    "Glenbrook,IL",
  "Lakeshore Conference,Westford,Owls,WSF,#7f1d1d,#ffffff,3,Westford,WI",
  "Prairie Athletic,Ridgeport Tech,Engineers,RPT,#166534,#e5e7eb,2," +
    "Ridgeport,IA",
].join("\n") + "\n";

const MAX_TEAMS = 400;

/**
 * Checks a team list and builds conferences and teams from it.
 * @param {object[]} rows one object per team, keyed by TEAM_CSV_COLUMNS
 *   (prestige optional, defaults to 3; city and state optional)
 * @return {object} {conferences, teams, errors}: conferences
 *   [{id, name, abbr, teamIds}], teams [{id, name, mascot, abbr, color1,
 *   color2, conference, prestige, city, state}] (city and state null when
 *   missing), errors [string] (empty when valid)
 */
export function buildTeamList(rows) {
  const errors = [];
  const teams = [];
  const conferences = new Map();
  const seen = {id: new Set(), abbr: new Set()};

  if (!Array.isArray(rows) || rows.length === 0) {
    return {conferences: [], teams: [], errors: ["The team list is empty."]};
  }
  if (rows.length > MAX_TEAMS) {
    errors.push(`A league can have at most ${MAX_TEAMS} teams.`);
  }

  rows.slice(0, MAX_TEAMS).forEach((row, i) => {
    const line = `Row ${i + 2}`; // row 1 is the header in a spreadsheet
    const text = (key, max) => String(row?.[key] ?? "").trim().slice(0, max);
    const confName = text("conference", 50);
    const name = text("name", 40);
    const mascot = text("mascot", 30);
    const abbr = text("abbreviation", 5).toUpperCase();
    const color1 = normalizeColor(row?.color1);
    const color2 = normalizeColor(row?.color2);
    const prestigeRaw = String(row?.prestige ?? "").trim();
    const prestige = prestigeRaw === "" ? 3 : Number(prestigeRaw);
    const city = text("city", 40) || null;
    const state = cleanState(row?.state);

    if (!confName) errors.push(`${line}: missing conference.`);
    if (!name) errors.push(`${line}: missing team name.`);
    if (!mascot) errors.push(`${line}: missing mascot.`);
    if (!/^[A-Z0-9]{2,5}$/.test(abbr)) {
      errors.push(`${line}: abbreviation must be 2-5 letters or numbers.`);
    }
    if (!color1 || !color2) {
      errors.push(`${line}: colors must be hex codes like #1d4ed8.`);
    }
    if (!Number.isInteger(prestige) || prestige < 1 || prestige > 5) {
      errors.push(`${line}: prestige must be a whole number from 1 to 5.`);
    }

    const id = `t-${slugify(name)}`;
    if (name && seen.id.has(id)) {
      errors.push(`${line}: another team is already named "${name}".`);
    }
    if (abbr && seen.abbr.has(abbr)) {
      errors.push(`${line}: abbreviation ${abbr} is already used.`);
    }
    seen.id.add(id);
    seen.abbr.add(abbr);
    if (!confName || !name) return;

    const confId = `c-${slugify(confName)}`;
    if (!conferences.has(confId)) {
      conferences.set(confId, {
        id: confId, name: confName, abbr: abbreviate(confName), teamIds: [],
      });
    }
    conferences.get(confId).teamIds.push(id);
    teams.push({
      id, name, mascot, abbr, color1, color2, conference: confId, prestige,
      city, state,
    });
  });

  for (const conf of conferences.values()) {
    const n = conf.teamIds.length;
    if (n < CONFERENCE_MIN || n > CONFERENCE_MAX) {
      errors.push(`${conf.name} has ${n} teams; each conference needs ` +
        `${CONFERENCE_MIN}-${CONFERENCE_MAX}.`);
    }
  }
  return {conferences: [...conferences.values()], teams, errors};
}

/**
 * @param {*} value
 * @return {string|null} "#rrggbb", or null if not a hex color
 */
export function normalizeColor(value) {
  const s = String(value ?? "").trim().toLowerCase();
  const m = s.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (!m) return null;
  const hex = m[1].length === 3 ?
    m[1].split("").map((c) => c + c).join("") :
    m[1];
  return `#${hex}`;
}

/**
 * "Great Rivers Athletic Conference" -> "GRAC".
 * @param {string} name
 * @return {string}
 */
export function abbreviate(name) {
  const words = name.split(/\s+/).filter((w) => /[a-z0-9]/i.test(w));
  if (words.length === 1) return words[0].slice(0, 4).toUpperCase();
  return words.map((w) => w[0]).join("").toUpperCase().slice(0, 5);
}

/**
 * Black or white, whichever reads better on the given background.
 * @param {string} hex "#rrggbb"
 * @return {string}
 */
export function textColorOn(hex) {
  const c = normalizeColor(hex) ?? "#000000";
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(c.slice(i, i + 2), 16));
  // Relative luminance, sRGB approximation.
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum > 0.6 ? "#111111" : "#ffffff";
}
